"use client";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import DOMPurify from "isomorphic-dompurify";

interface LegalContentProps {
  src: string;
  className?: string;
}

const LegalContent: React.FC<LegalContentProps> = ({
  src,
  className = "tnc-cms-content",
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const [isRtl, setIsRtl] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intl = useIntl();
  const isArabic = intl.locale === "ar";

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    setIsRtl(false);

    // In Arabic, prefer the localized document under /legal/ar/ and fall back to
    // the English one when a given document has no Arabic version yet.
    const arabicSrc = src.replace("/legal/", "/legal/ar/");
    const loadDoc = async (): Promise<{ text: string; rtl: boolean }> => {
      if (isArabic) {
        const arRes = await fetch(arabicSrc);
        if (arRes.ok) return { text: await arRes.text(), rtl: true };
      }
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { text: await res.text(), rtl: false };
    };

    loadDoc()
      .then(({ text, rtl }) => {
        if (cancelled) return;
        // Keep target so external links (e.g. target="_blank") open in a new
        // tab on the standalone page / WebView (DOMPurify drops it by default).
        const sanitized = DOMPurify.sanitize(text, { ADD_ATTR: ["target"] });
        const wrapped = sanitized
          .replace(/<table\b/gi, '<div class="tnc-table-wrap"><table')
          .replace(/<\/table>/gi, "</table></div>");
        setHtml(wrapped);
        setIsRtl(rtl);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [src, isArabic]);

  if (error) {
    return (
      <p className="tnc-tab-empty">{intl.formatMessage({ id: "noData" })}</p>
    );
  }
  if (html === null) {
    return null;
  }
  // Content is sanitized via DOMPurify above before render
  const sanitizedMarkup = { __html: html };
  return (
    <div
      className={className}
      dir={isRtl ? "rtl" : undefined}
      dangerouslySetInnerHTML={sanitizedMarkup}
    />
  );
};

export default LegalContent;
